import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeSv from '@angular/common/locales/sv';
import { ApplicationConfig, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withPreloading, withViewTransitions } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@environments/environment';
import { LOCAL_STORAGE_ENGINE, SESSION_STORAGE_ENGINE, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { defineLocale, svLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { provideToastr } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';
import { resolveAppPreset } from './core/config/prime-theme.config';
import { SelectivePreloadingStrategy } from './core/config/selective-preloading.strategy';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { AuthContextState } from './shared/state/auth-context/auth-context.state';
import { DomainContextState } from './shared/state/domain-context/domain-context.state';
import { PageContextState } from './shared/state/page-context/page-context.state';
import { UserContextState } from './shared/state/user-context/user-context.state';


registerLocaleData(localeSv, 'sv-SE');
defineLocale('sv', svLocale);

const swedishPrimeNgTranslation = {
  startsWith: 'Börjar med',
  contains: 'Innehåller',
  notContains: 'Innehåller inte',
  endsWith: 'Slutar med',
  equals: 'Lika med',
  notEquals: 'Inte lika med',
  noFilter: 'Inget filter',
  lt: 'Mindre än',
  lte: 'Mindre än eller lika med',
  gt: 'Större än',
  gte: 'Större än eller lika med',
  is: 'Är',
  isNot: 'Är inte',
  before: 'Före',
  after: 'Efter',
  clear: 'Rensa',
  apply: 'Använd',
  matchAll: 'Matcha alla',
  matchAny: 'Matcha någon',
  addRule: 'Lägg till regel',
  removeRule: 'Ta bort regel',
  accept: 'Ja',
  reject: 'Nej',
  choose: 'Välj',
  upload: 'Ladda upp',
  cancel: 'Avbryt',
  dayNames: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
  dayNamesShort: ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'],
  dayNamesMin: ['S', 'M', 'T', 'O', 'T', 'F', 'L'],
  monthNames: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
  monthNamesShort: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
  firstDayOfWeek: 1,
  today: 'I dag',
  weekHeader: 'v',
  weak: 'Svagt',
  medium: 'Medel',
  strong: 'Starkt',
  passwordPrompt: 'Ange ett lösenord',
  emptyMessage: 'Inga resultat hittades',
  emptyFilterMessage: 'Inga resultat hittades',
  chooseYear: 'Välj år',
  chooseMonth: 'Välj månad',
  chooseDate: 'Välj datum',
  prevDecade: 'Föregående årtionde',
  nextDecade: 'Nästa årtionde',
  prevYear: 'Föregående år',
  nextYear: 'Nästa år',
  prevMonth: 'Föregående månad',
  nextMonth: 'Nästa månad',
  prevHour: 'Föregående timme',
  nextHour: 'Nästa timme',
  prevMinute: 'Föregående minut',
  nextMinute: 'Nästa minut',
  aria: {
    trueLabel: 'Sant',
    falseLabel: 'Falskt',
    nullLabel: 'Inte valt',
    selectAll: 'Markera alla',
    unselectAll: 'Avmarkera alla',
    close: 'Stäng',
    previous: 'Föregående',
    next: 'Nästa',
    pageLabel: 'Sida',
    firstPageLabel: 'Första sidan',
    lastPageLabel: 'Sista sidan',
    nextPageLabel: 'Nästa sida',
    prevPageLabel: 'Föregående sida',
    rowsPerPageLabel: 'Rader per sida',
    selectRow: 'Markera rad',
    unselectRow: 'Avmarkera rad',
    expandRow: 'Expandera rad',
    collapseRow: 'Fäll ihop rad'
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions(), withPreloading(SelectivePreloadingStrategy)),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideToastr({
      positionClass: 'toast-top-left',
      timeOut: 5000,
      preventDuplicates: true,
      closeButton: true,
      maxOpened: 5,
      tapToDismiss: true,
      progressBar: true,
      progressAnimation: 'decreasing'
    }),
    importProvidersFrom(
      TooltipModule.forRoot(), // Import TooltipModule as a provider
      BsDatepickerModule.forRoot(),
      TimepickerModule.forRoot(),
      ModalModule.forRoot(),
      TabsModule.forRoot()
    ),
    providePrimeNG({
      ripple: true,
      zIndex: {
        modal: 1100,
        overlay: 1100,
        menu: 1000,
        tooltip: 1100
      },
      translation: swedishPrimeNgTranslation,
      theme: {
        preset: resolveAppPreset(),
        options: {
          darkModeSelector: false
        }
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'sv-SE' },
    provideStore(
      [UserContextState, AuthContextState, DomainContextState, PageContextState],
      withNgxsStoragePlugin({
        keys: [
          { key: 'pageContext', engine: SESSION_STORAGE_ENGINE },
          { key: 'authContext', engine: LOCAL_STORAGE_ENGINE },
          { key: 'userContext', engine: LOCAL_STORAGE_ENGINE },
          { key: 'domainContext', engine: LOCAL_STORAGE_ENGINE }
        ]
      })
    ),
    // provideAppInitializer(() => { // uncomment when we are ready to use idle.service
    //   inject(IdleService);
    // }),
  ]
};
